import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem, transparentize } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Image from 'components/Common/Image'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { scrollToId } from 'lib/window/scrollUtils'
import Sheet from 'components/utils/Sheet'
import { TOUR_DEFAULT_DEAL_HIGHLIGHT_COLOUR } from 'constants/tours'

const ImageBackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 50%;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 80%;
    background: linear-gradient(to top, var(--hero-custom-colour), var(--hero-custom-colour-80), rgb(0 0 0 / 0%));
    z-index: 1;
  }

  ${mediaQueryUp.tablet} {
    inset-inline-end: 0;
    width: 60%;
    height: 100%;

    &::before {
      top: 0;
      inset-inline-start: 0;
      width: ${rem(400)};
      height: 100%;
      background: linear-gradient(to right, var(--hero-custom-colour), rgb(0 0 0 / 0%));
    }

    [dir='rtl'] &::before {
      background: linear-gradient(to left, var(--hero-custom-colour), rgb(0 0 0 / 0%));
    }
  }

  ${mediaQueryUp.desktop} {
    width: 70%;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: ${rem(300)};
  padding-top: ${rem(32)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.tablet} {
    width: 50%;
    padding-inline-start: ${rem(16)};
    padding-top: ${rem(40)};
    padding-bottom: ${rem(40)};
    align-content: center;
  }
`

const TourDealHeroWrapper = styled(Sheet)`
  position: relative;
`

const ContentGroup = styled(Group)`
  height: ${rem(190)};
`

const FooterGroup = styled(Group)`
  height: ${rem(32)};
`

const DEFAULT_HERO_IMAGE_ID = ''

interface Props {
  tourDeal: App.Tours.TourDealDetails
}

function TourDealHero({ tourDeal }: Props) {
  const intl = useIntl()
  const handleScrollToOffers = useCallback(() => {
    scrollToId('tour-deal-offer-list')
  }, [])

  const shouldShowViewOffers = useMemo(() => {
    return tourDeal.tours.length > 0
  }, [tourDeal])

  return <TourDealHeroWrapper
    paletteType="inverse"
    style={{ backgroundColor: tourDeal.highlightColour }}
  >
    <ImageBackgroundWrapper style={{
      '--hero-custom-colour': tourDeal.highlightColour,
      '--hero-custom-colour-80': transparentize(0.8, tourDeal.highlightColour ?? TOUR_DEFAULT_DEAL_HIGHLIGHT_COLOUR),
    }}
    >
      <Image
        alt={intl.formatMessage(
          {
            id: 'mL4GlUmrSHkXRWKZC3gF',
            defaultMessage: 'Hero image',
            description: 'Alt text for the hero banner image on the tour deal page',
          },
        )}
        id={tourDeal.heroImageId || DEFAULT_HERO_IMAGE_ID}
        fit="center"
      />
    </ImageBackgroundWrapper>

    <LayoutContainer size="xl">
      <ContentWrapper>
        <CSSBreakpoint min="tablet">
          <Group direction="vertical" gap={16} verticalAlign="center" fullHeight>
            <Heading variant="heading2">
              {tourDeal.heading}
            </Heading>
            <BodyText variant="medium">
              {tourDeal.subHeading}&nbsp;
            </BodyText>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <Group direction="vertical" gap={16} verticalAlign="center" fullHeight>
            <ContentGroup direction="vertical" gap={16} horizontalAlign="start" verticalAlign="end">
              <Heading variant="heading2">
                {tourDeal.heading}
              </Heading>
              {tourDeal.subHeading && <BodyText variant="medium">
                {tourDeal.subHeading}
              </BodyText>}
            </ContentGroup>
            <FooterGroup direction="horizontal" gap={8} horizontalAlign="end" verticalAlign="end" fullHeight>
              {shouldShowViewOffers && <TextButton
                kind="primary"
                onClick={handleScrollToOffers}
              >
                {intl.formatMessage(
                  {
                    id: '-_7RKMT8UBXienRxUsKo',
                    defaultMessage: 'View offers',
                    description: 'Button label to scroll down to view tour deal offers',
                  },
                )}
              </TextButton>}
            </FooterGroup>
          </Group>
        </CSSBreakpoint>
      </ContentWrapper>
    </LayoutContainer>
  </TourDealHeroWrapper>
}

export default TourDealHero
